import React from "react"
import LandingPage from "."
import PropTypes from "prop-types"
import { graphqlDynamic } from "react-apollo-dynamic-query"
import gql from "graphql-tag"
import { connect } from "react-redux"
import moment from "moment"
import { GRAPHQL_DATE_FORMAT } from "../../helpers/sharedConstants"
import { complianceNumberConverter } from "@swipesense/compliance-helpers"

const facilitiesQuery = ({ bsrCompliance }) => gql`
  query LandingPageFacilities(
    $entityIds: [ID!]!
    $startAt: String!
    $endAt: String!
  ) {
    entities: facilities(ids: $entityIds) {
      id
      name
      __typename
      compliance: ${
        bsrCompliance ? "bsrCompliance" : "roundingCompliance"
      }(startAt: $startAt, endAt: $endAt) {
        timeslices {
          full {
            aggregates {
              overall {
                compliancePercentage
                ${
                  bsrCompliance
                    ? "compliantOpportunityCount"
                    : "compliantRoundCount"
                }
                ${bsrCompliance ? "opportunityCount" : "roundCount"}
              }
            }
          }
        }
      }
      departmentsConnection {
        totalCount
      }
      unitsConnection {
        totalCount
      }
    }
  }
`

const departmentsQuery = ({ bsrCompliance }) => gql`
  query LandingPageDepartments(
    $entityIds: [ID!]!
    $startAt: String!
    $endAt: String!
  ) {
    entities: departments(ids: $entityIds) {
      id
      name
      __typename
      compliance: ${
        bsrCompliance ? "bsrCompliance" : "roundingCompliance"
      }(startAt: $startAt, endAt: $endAt) {
        timeslices {
          full {
            aggregates {
              overall {
                compliancePercentage
                ${
                  bsrCompliance
                    ? "compliantOpportunityCount"
                    : "compliantRoundCount"
                }
                ${bsrCompliance ? "opportunityCount" : "roundCount"}
              }
            }
          }
        }
      }
      usersConnection {
        totalCount
      }
      facility {
        id
        name
      }
    }
  }
`

const entityTypeToQuery = {
  facilities: facilitiesQuery,
  departments: departmentsQuery,
}

const entityMapping = entity => {
  const aggs = entity.compliance.timeslices.full.aggregates.overall
  const compliance = aggs.compliancePercentage
  const opportunities =
    aggs.opportunityCount === undefined
      ? aggs.roundCount
      : aggs.opportunityCount

  const entityForLandingPage = {
    id: entity.id,
    name: entity.name,
    opportunities: opportunities,
    compliance: compliance,
  }

  switch (entity.__typename) {
    case "Facility":
      entityForLandingPage.departmentCount =
        entity.departmentsConnection.totalCount
      entityForLandingPage.unitCount = entity.unitsConnection.totalCount
      break
    case "Department":
      entityForLandingPage.staffCount = entity.usersConnection.totalCount
      entityForLandingPage.facilityName = entity.facility.name
      break
    /* istanbul ignore next */
    default:
      throw new Error(`Unexpected type: ${entity.__typename}`)
  }

  return entityForLandingPage
}

const sortEntities = entities =>
  entities.sort(
    (a, b) =>
      complianceNumberConverter(b.compliance, b.opportunities) -
      complianceNumberConverter(a.compliance, a.opportunities)
  )

const generateComponent = props => {
  const query = entityTypeToQuery[props.entityType]
  return graphqlDynamic(query, {
    options: ({ entityIds, startAt, endAt }) => {
      return {
        variables: {
          entityIds,
          startAt: startAt.format(GRAPHQL_DATE_FORMAT),
          endAt: endAt.format(GRAPHQL_DATE_FORMAT),
        },
      }
    },
    props: ({ data: { error, loading, entities } }) => {
      if (error) {
        throw error
      }
      if (!loading) {
        return {
          entityType: props.entityType,
          entities: sortEntities(entities.map(entityMapping)),
        }
      } else {
        return { loading }
      }
    },
  })(LandingPage)
}

const LandingPageWithApollo = props => {
  const Component = generateComponent(props)
  return <Component {...props} />
}

const LandingPageWithData = connect(
  ({
    dateRange: { startDate, endDate },
    bsrCompliance: { bsrCompliance },
  }) => ({
    startAt: moment(startDate),
    endAt: moment(endDate),
    bsrCompliance,
  })
)(LandingPageWithApollo)

LandingPageWithData.propTypes = {
  entityType: PropTypes.oneOf(["facilities", "departments"]).isRequired,
  entityIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
}

export default LandingPageWithData
